import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { LanguageToggle } from "@/components/LanguageToggle";
import {
  listBookings, updateBookingStatus, getSettings, updateSettings,
  adminLogin, adminVerify, getToken, setToken, clearToken,
} from "@/lib/api";
import { AdminProducts } from "@/components/AdminProducts";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ArrowLeft, CheckCircle, XCircle, Gear, Lock, SignOut, Sparkle } from "@phosphor-icons/react";

const todayStr = () => new Date().toISOString().slice(0, 10);

const STATUS_STYLE = {
  pending: "bg-[#E7E1D2] text-[#24483A]",
  completed: "bg-[#24483A] text-white",
  cancelled: "bg-[#F0F0F0] text-[#A3A3A3] line-through",
};

const LoginGate = ({ onSuccess }) => {
  const { t } = useLang();
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { token } = await adminLogin(pin);
      setToken(token);
      onSuccess();
    } catch {
      toast.error(t.admin.auth.wrong);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F1EEE4] px-5 font-sans">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm rounded-2xl border border-[#20261E]/10 bg-white p-8 shadow-[0_8px_32px_rgba(28,37,29,0.08)]"
      >
        <div className="mb-6 flex items-center gap-2">
          <Sparkle size={22} weight="fill" className="text-[#24483A]" />
          <span className="font-serif text-lg tracking-tight text-[#20261E]">Michalis Christofi</span>
        </div>
        <div className="mb-2 flex items-center gap-2 text-[#24483A]">
          <Lock size={20} weight="fill" />
          <h1 className="font-serif text-2xl tracking-tight">{t.admin.auth.title}</h1>
        </div>
        <p className="mb-6 text-sm text-[#8F8A79]">{t.admin.auth.subtitle}</p>
        <Label className="mb-2 block text-sm text-[#5B6053]">{t.admin.auth.pin}</Label>
        <Input
          data-testid="admin-pin-input"
          type="password"
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          autoFocus
          className="rounded-xl border-[#20261E]/10"
        />
        <Button
          data-testid="admin-login-btn"
          type="submit"
          disabled={loading || !pin}
          className="mt-5 w-full rounded-full bg-[#24483A] py-4 text-white hover:bg-[#163024] disabled:opacity-50"
        >
          {t.admin.auth.enter}
        </Button>
        <Link to="/" className="mt-4 block text-center text-sm text-[#8F8A79] hover:text-[#20261E]">
          {t.admin.back}
        </Link>
      </form>
    </div>
  );
};

export default function AdminPage() {
  const { t, lang } = useLang();
  const [authState, setAuthState] = useState("checking"); // checking | in | out
  const [date, setDate] = useState(todayStr());
  const [bookings, setBookings] = useState([]);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    if (!getToken()) { setAuthState("out"); return; }
    adminVerify().then(() => setAuthState("in")).catch(() => { clearToken(); setAuthState("out"); });
  }, []);

  const load = useCallback(async (d) => {
    const data = await listBookings(d);
    setBookings(data);
  }, []);

  useEffect(() => { if (authState === "in") load(date); }, [date, load, authState]);
  useEffect(() => { if (authState === "in") getSettings().then(setSettings); }, [authState]);

  const setStatus = async (id, status) => {
    try { await updateBookingStatus(id, status); load(date); }
    catch { toast.error(t.errors.generic); }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const updated = await updateSettings(settings);
      setSettings(updated);
      toast.success(t.admin.saved);
    } catch { toast.error(t.errors.generic); }
    finally { setSavingSettings(false); }
  };

  const logout = () => { clearToken(); setAuthState("out"); };

  if (authState === "checking") {
    return <div className="flex min-h-screen items-center justify-center bg-[#F1EEE4] text-[#8F8A79]">…</div>;
  }
  if (authState === "out") {
    return <LoginGate onSuccess={() => setAuthState("in")} />;
  }

  return (
    <div className="min-h-screen bg-[#F1EEE4] font-sans text-[#20261E]">
      <header className="sticky top-0 z-50 border-b border-black/5 bg-white/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-4">
          <Link data-testid="back-to-booking" to="/" className="flex items-center gap-2 text-sm font-medium text-[#5B6053] hover:text-[#20261E]">
            <ArrowLeft size={18} /> {t.admin.back}
          </Link>
          <div className="flex items-center gap-3">
            <button data-testid="admin-logout-btn" onClick={logout}
              className="flex items-center gap-1.5 text-sm font-medium text-[#5B6053] hover:text-[#20261E]">
              <SignOut size={16} /> {t.admin.auth.logout}
            </button>
            <LanguageToggle />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-10">
        <h1 className="font-serif text-3xl tracking-tight sm:text-4xl">{t.admin.title}</h1>

        <div className="mt-6 flex items-end gap-4">
          <div>
            <Label className="mb-2 block text-xs uppercase tracking-[0.05em] text-[#8F8A79]">{t.admin.date}</Label>
            <Input data-testid="admin-date-input" type="date" value={date}
              onChange={(e) => setDate(e.target.value)}
              className="rounded-xl border-[#20261E]/10 bg-white" />
          </div>
        </div>

        <div data-testid="bookings-list" className="mt-6 space-y-3">
          {bookings.length === 0 && (
            <p className="rounded-2xl border border-[#20261E]/10 bg-white p-8 text-center text-[#8F8A79]">
              {t.admin.none}
            </p>
          )}
          {bookings.map((b) => (
            <div key={b.id} data-testid={`booking-${b.id}`}
              className="rounded-2xl border border-[#20261E]/10 bg-white p-5 shadow-[0_8px_32px_rgba(28,37,29,0.04)]">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="rounded-xl bg-[#E7E1D2] px-4 py-2 text-center">
                    <div className="font-serif text-lg font-bold text-[#24483A]">{b.slot_time}</div>
                  </div>
                  <div>
                    <div className="font-semibold">{b.name}</div>
                    <div className="text-sm text-[#5B6053]">
                      {lang === "gr" ? b.service_label_gr : b.service_label_en} · €{b.price}
                    </div>
                    <div className="text-sm text-[#8F8A79]">{b.phone} · {b.email}</div>
                    {b.is_minor && (
                      <div className="mt-1 text-xs text-[#24483A]">
                        {lang === "gr" ? "Ανήλικος" : "Minor"}{b.parent_name ? ` · ${b.parent_name}` : ""}
                      </div>
                    )}
                    {b.health_nickel_allergy && (
                      <div className="text-xs font-medium text-red-600">
                        {lang === "gr" ? "Αλλεργία νικελίου" : "Nickel allergy"}
                      </div>
                    )}
                    {b.health_note && <div className="text-xs text-[#8F8A79]">{b.health_note}</div>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-3 py-1 text-xs font-medium ${STATUS_STYLE[b.status]}`}>
                    {t.admin.status[b.status]}
                  </span>
                  {b.status !== "completed" && (
                    <Button data-testid={`complete-${b.id}`} onClick={() => setStatus(b.id, "completed")}
                      className="h-9 rounded-full bg-[#24483A] px-3 text-xs text-white hover:bg-[#163024]">
                      <CheckCircle size={16} className="mr-1" /> {t.admin.markComplete}
                    </Button>
                  )}
                  {b.status !== "cancelled" && (
                    <Button data-testid={`cancel-${b.id}`} variant="outline" onClick={() => setStatus(b.id, "cancelled")}
                      className="h-9 rounded-full border-[#20261E]/10 px-3 text-xs text-[#5B6053]">
                      <XCircle size={16} className="mr-1" /> {t.admin.markCancel}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {settings && (
          <div className="mt-12 rounded-2xl border border-[#20261E]/10 bg-white p-8 shadow-[0_8px_32px_rgba(28,37,29,0.04)]">
            <div className="flex items-center gap-3">
              <Gear size={22} className="text-[#24483A]" />
              <h2 className="font-serif text-2xl tracking-tight">{t.admin.season}</h2>
            </div>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                ["summer_start", t.admin.summerStart],
                ["summer_end", t.admin.summerEnd],
                ["summer_evening", t.admin.summerEvening],
                ["winter_evening", t.admin.winterEvening],
              ].map(([key, label]) => (
                <div key={key}>
                  <Label className="mb-2 block text-xs uppercase tracking-[0.05em] text-[#8F8A79]">{label}</Label>
                  <Input data-testid={`setting-${key}`} value={settings[key] || ""}
                    onChange={(e) => setSettings({ ...settings, [key]: e.target.value })}
                    className="rounded-xl border-[#20261E]/10" />
                </div>
              ))}
            </div>
            <Button data-testid="save-settings" disabled={savingSettings} onClick={saveSettings}
              className="mt-6 rounded-full bg-[#24483A] px-8 py-4 text-white hover:bg-[#163024]">
              {t.admin.save}
            </Button>
          </div>
        )}

        <AdminProducts />
      </main>
    </div>
  );
}
